import React, { useState } from 'react';
import { useOrganization } from '../context/OrganizationContext';
import { supabase } from '../lib/supabase';

export default function OrganizationSettingsModal({ isOpen, onClose, canEdit }) {
  const { currentOrganization, loadCurrentOrganization } = useOrganization();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: currentOrganization?.name || '',
    description: currentOrganization?.description || '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: updateError } = await supabase
        .from('organizations')
        .update({
          name: formData.name,
          description: formData.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrganization.id);

      if (updateError) throw updateError;

      setSuccess(true);
      loadCurrentOrganization();
      
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating organization:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-dark-200 rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Organization Settings</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Organization Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 bg-dark-300 border border-dark-400 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-white ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={!canEdit}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className={`w-full px-3 py-2 bg-dark-300 border border-dark-400 rounded focus:outline-none focus:ring-2 focus:ring-accent-primary text-white ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                rows={3}
                disabled={!canEdit}
              />
            </div>

            {!canEdit && (
              <div className="text-sm text-gray-400 italic">
                Only administrators and owners can modify organization settings
              </div>
            )}

            {error && (
              <div className="text-accent-error text-sm">{error}</div>
            )}

            {success && (
              <div className="text-accent-success text-sm">Settings updated successfully</div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:bg-dark-300 rounded"
            >
              Close
            </button>
            
            {canEdit && (
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-accent-primary text-dark-100 rounded hover:bg-opacity-90 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
} 